import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { isEmbedded } from '../embed';

export type Mode = 'preview' | 'diff' | 'terminal' | 'files' | 'tasks' | 'plan';
export type InspectorLayout = 'single' | 'split-h' | 'split-v' | 'grid-2x2';
export interface Pane { id: string; mode: Mode }

export const LAYOUT_CAPACITY: Record<InspectorLayout, number> = {
  'single': 1,
  'split-h': 2,
  'split-v': 2,
  'grid-2x2': 4,
};

export const INSPECTOR_MIN = 200;
export const INSPECTOR_DEFAULT = 360;
// Upper bound is dynamic — viewport width minus rail and a minimal chat column.
// Resolved at drag time so it scales with window size and doesn't hard-cap the user.
export const INSPECTOR_RESERVED_FOR_REST = 320; // 224 rail + ~96 min chat breathing room

export interface ApiKeys {
  openai?: string;
  anthropic?: string;
  gemini?: string;
}

export type PermissionMode = 'default' | 'acceptEdits' | 'auto' | 'plan' | 'bypassPermissions';

interface UIStore {
  /** Mode of the active (focused) pane — kept as a derived field for back-compat. */
  mode: Mode;
  /** Sets the focused pane's mode (or pane[0] in single-pane). */
  setMode: (m: Mode) => void;

  /** Inspector pane layout. */
  inspectorLayout: InspectorLayout;
  setInspectorLayout: (l: InspectorLayout) => void;
  /** Ordered list of panes — length capped at LAYOUT_CAPACITY[layout]. */
  inspectorPanes: Pane[];
  /** Pane id with input focus / shown as "active". */
  activePaneId: string;
  setActivePaneId: (id: string) => void;
  /** Add a pane in the next free slot. No-op if layout is full. */
  addPane: (mode: Mode) => void;
  /** Remove a pane (caller is responsible for ensuring at least one remains). */
  removePane: (id: string) => void;
  /** Change the mode of a specific pane. */
  setPaneMode: (id: string, mode: Mode) => void;

  permissionMode: PermissionMode;
  setPermissionMode: (m: PermissionMode) => void;

  slashOpen: boolean;
  setSlashOpen: (b: boolean) => void;

  inspectorOpen: boolean;
  setInspectorOpen: (b: boolean) => void;
  toggleInspector: () => void;

  inspectorWidth: number;
  setInspectorWidth: (n: number) => void;

  railOpen: boolean;
  setRailOpen: (b: boolean) => void;
  toggleRail: () => void;

  settingsOpen: boolean;
  setSettingsOpen: (b: boolean) => void;

  apiKeys: ApiKeys;
  setApiKey: (provider: keyof ApiKeys, value: string) => void;

  /**
   * One-shot insert into the composer draft. Set by the Preview pane's
   * select-element tool; consumed (cleared) by the Composer on next render.
   * Lets non-composer components inject text without owning the draft.
   */
  pendingComposerInsert: string | null;
  setPendingComposerInsert: (s: string | null) => void;
}

function paneId(): string {
  return `p_${Math.random().toString(36).slice(2, 8)}`;
}

const INITIAL_PANE: Pane = { id: paneId(), mode: 'preview' };

export const useUIStore = create<UIStore>()(persist((set) => ({
  mode: 'preview',
  setMode: (mode) =>
    set((s) => {
      const id = s.activePaneId || s.inspectorPanes[0]?.id;
      const panes = s.inspectorPanes.map((p) => (p.id === id ? { ...p, mode } : p));
      return { mode, inspectorPanes: panes };
    }),

  inspectorLayout: 'single',
  setInspectorLayout: (inspectorLayout) =>
    set((s) => {
      const cap = LAYOUT_CAPACITY[inspectorLayout];
      const panes = s.inspectorPanes.slice(0, cap);
      // Ensure activePaneId still valid
      const activePaneId = panes.find((p) => p.id === s.activePaneId)?.id ?? panes[0]?.id ?? '';
      return { inspectorLayout, inspectorPanes: panes, activePaneId };
    }),
  inspectorPanes: [INITIAL_PANE],
  activePaneId: INITIAL_PANE.id,
  setActivePaneId: (id) => set({ activePaneId: id }),
  addPane: (mode) =>
    set((s) => {
      const cap = LAYOUT_CAPACITY[s.inspectorLayout];
      if (s.inspectorPanes.length >= cap) return s;
      const p = { id: paneId(), mode };
      return { inspectorPanes: [...s.inspectorPanes, p], activePaneId: p.id, mode };
    }),
  removePane: (id) =>
    set((s) => {
      const panes = s.inspectorPanes.filter((p) => p.id !== id);
      if (panes.length === 0) panes.push({ id: paneId(), mode: 'preview' });
      const activePaneId =
        panes.find((p) => p.id === s.activePaneId)?.id ?? panes[0].id;
      const mode = panes.find((p) => p.id === activePaneId)?.mode ?? s.mode;
      return { inspectorPanes: panes, activePaneId, mode };
    }),
  setPaneMode: (id, mode) =>
    set((s) => ({
      inspectorPanes: s.inspectorPanes.map((p) => (p.id === id ? { ...p, mode } : p)),
      mode: id === s.activePaneId ? mode : s.mode,
    })),

  permissionMode: 'auto',
  setPermissionMode: (permissionMode) => set({ permissionMode }),

  slashOpen: false,
  setSlashOpen: (slashOpen) => set({ slashOpen }),

  inspectorOpen: !isEmbedded,
  setInspectorOpen: (inspectorOpen) => set({ inspectorOpen }),
  toggleInspector: () => set((s) => ({ inspectorOpen: !s.inspectorOpen })),

  inspectorWidth: INSPECTOR_DEFAULT,
  setInspectorWidth: (w) => {
    const max = Math.max(
      INSPECTOR_MIN + 1,
      (typeof window !== 'undefined' ? window.innerWidth : 1440) - INSPECTOR_RESERVED_FOR_REST
    );
    set({ inspectorWidth: Math.max(INSPECTOR_MIN, Math.min(max, Math.round(w))) });
  },

  settingsOpen: false,
  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),

  railOpen: !isEmbedded,
  setRailOpen: (railOpen) => set({ railOpen }),
  toggleRail: () => set((s) => ({ railOpen: !s.railOpen })),

  apiKeys: {},
  setApiKey: (provider, value) =>
    set((s) => ({ apiKeys: { ...s.apiKeys, [provider]: value || undefined } })),

  pendingComposerInsert: null,
  setPendingComposerInsert: (s) => set({ pendingComposerInsert: s }),
}), {
  name: 'xoro.ui.v1',
  storage: createJSONStorage(() => localStorage),
  partialize: (state) => ({
    mode: state.mode,
    inspectorLayout: state.inspectorLayout,
    inspectorPanes: state.inspectorPanes,
    activePaneId: state.activePaneId,
    inspectorOpen: state.inspectorOpen,
    inspectorWidth: state.inspectorWidth,
    railOpen: state.railOpen,
    permissionMode: state.permissionMode,
    apiKeys: state.apiKeys,
  }),
  // Migrate the deprecated `bypass: boolean` field to permissionMode
  // so users with persisted state get a sensible mode rather than
  // losing their preference.
  migrate: (persistedState, _version) => {
    const s = persistedState as Partial<UIStore> & { bypass?: boolean } | null | undefined;
    if (!s) return s;
    let next: Partial<UIStore> & { bypass?: boolean } = { ...s };
    if (typeof next.permissionMode === 'undefined') {
      next = { ...next, permissionMode: next.bypass ? 'bypassPermissions' : 'acceptEdits' };
    }
    // v3: introduce inspectorLayout + inspectorPanes; seed from old `mode`.
    if (!next.inspectorPanes || !Array.isArray(next.inspectorPanes) || next.inspectorPanes.length === 0) {
      const id = paneId();
      const mode = (next.mode as Mode | undefined) ?? 'preview';
      next = {
        ...next,
        inspectorLayout: 'single',
        inspectorPanes: [{ id, mode }],
        activePaneId: id,
      };
    }
    return next;
  },
  version: 3,
}));
